
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar } from 'recharts';
import { Cloud, TrendingUp, Thermometer, DollarSign } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { ClimateScenario, ClimateRisk, ClimateRiskScenario } from '@/types/climate';
import { useToast } from '@/hooks/use-toast';

export const ScenarioAnalysis = () => {
  const [scenarios, setScenarios] = useState<ClimateScenario[]>([]);
  const [risks, setRisks] = useState<ClimateRisk[]>([]);
  const [riskScenarios, setRiskScenarios] = useState<ClimateRiskScenario[]>([]);
  const [selectedScenario, setSelectedScenario] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [scenariosRes, risksRes, riskScenariosRes] = await Promise.all([
        supabase.from('climate_scenarios' as any).select('*').order('created_at', { ascending: false }),
        supabase.from('climate_risks' as any).select('*'),
        supabase.from('climate_risk_scenarios' as any).select('*')
      ]);

      const scenarioData = (scenariosRes.data as unknown as ClimateScenario[]) || [];
      setScenarios(scenarioData);
      setRisks((risksRes.data as unknown as ClimateRisk[]) || []);
      setRiskScenarios((riskScenariosRes.data as unknown as ClimateRiskScenario[]) || []);
      
      // Set default scenario
      if (scenarioData.length > 0 && !selectedScenario) {
        setSelectedScenario(scenarioData[0].id);
      }
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const runScenarioAnalysis = async () => {
    if (risks.length === 0) {
      toast({
        title: "No Risks Found",
        description: "Please run risk detection first to analyze scenarios",
        variant: "destructive",
      });
      return;
    }

    setAnalyzing(true);
    
    try {
      const scenario = scenarios.find(s => s.id === selectedScenario);
      if (!scenario) throw new Error('Selected scenario not found');

      // Generate scenario-adjusted risk data
      const adjustedRisks = risks.map(risk => {
        const adjustmentFactor = calculateScenarioAdjustment(risk, scenario);
        
        return {
          risk_id: risk.id,
          scenario_id: scenario.id,
          adjusted_probability: Math.min(100, (risk.probability_score || 0) * adjustmentFactor.probability),
          adjusted_impact: Math.min(100, (risk.impact_score || 0) * adjustmentFactor.impact),
          adjusted_financial_impact_low: (risk.financial_impact_low || 0) * adjustmentFactor.financial,
          adjusted_financial_impact_high: (risk.financial_impact_high || 0) * adjustmentFactor.financial,
          scenario_notes: `Adjusted for ${scenario.scenario_name} scenario`
        };
      });

      // Delete existing mappings for this scenario
      await supabase
        .from('climate_risk_scenarios' as any)
        .delete()
        .eq('scenario_id', scenario.id);

      // Insert new mappings
      const { error } = await supabase
        .from('climate_risk_scenarios' as any)
        .insert(adjustedRisks);

      if (error) throw error;

      toast({
        title: "Success",
        description: `Scenario analysis completed for ${scenario.scenario_name}`,
      });
      
      loadData();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to run scenario analysis",
        variant: "destructive",
      });
    } finally {
      setAnalyzing(false);
    }
  };

  const calculateScenarioAdjustment = (risk: ClimateRisk, scenario: ClimateScenario) => {
    let probabilityFactor = 1;
    let impactFactor = 1;
    let financialFactor = 1;

    // Adjust based on scenario type and pathway
    switch (scenario.pathway) {
      case 'net_zero_2050':
        // Lower physical risks, higher transition risks
        if (risk.risk_type?.startsWith('physical')) {
          probabilityFactor = 0.8;
          impactFactor = 0.9;
        } else if (risk.risk_type?.startsWith('transition')) {
          probabilityFactor = 1.4;
          impactFactor = 1.3;
        }
        break;
      
      case '4c_future':
        // Higher physical risks, lower transition risks
        if (risk.risk_type?.startsWith('physical')) {
          probabilityFactor = 1.6;
          impactFactor = 1.8;
          financialFactor = 2.0;
        } else if (risk.risk_type?.startsWith('transition')) {
          probabilityFactor = 0.7;
          impactFactor = 0.8;
        }
        break;
      
      case 'below_2c':
        // Moderate adjustments
        if (risk.risk_type?.startsWith('physical')) {
          probabilityFactor = 1.1;
          impactFactor = 1.2;
        } else if (risk.risk_type?.startsWith('transition')) {
          probabilityFactor = 1.2;
          impactFactor = 1.1;
        }
        break;
      
      case 'current_policies':
        // Gradual increase in physical risks
        if (risk.risk_type?.startsWith('physical')) {
          probabilityFactor = 1.3;
          impactFactor = 1.4;
          financialFactor = 1.5;
        }
        break;
    }

    // Additional adjustments based on specific risk categories
    if (scenario.temperature_increase && scenario.temperature_increase > 2) {
      if (['extreme_heat', 'drought', 'wildfire'].includes(risk.risk_category)) {
        probabilityFactor *= 1.3;
        impactFactor *= 1.4;
      }
    }

    if (scenario.carbon_price && scenario.carbon_price > 100) {
      if (risk.risk_category === 'carbon_pricing') {
        probabilityFactor *= 1.5;
        impactFactor *= 1.6;
        financialFactor *= 2.0;
      }
    }

    return { probability: probabilityFactor, impact: impactFactor, financial: financialFactor };
  };

  const getScenarioAdjustedRisks = () => {
    if (!selectedScenario) return [];
    
    return riskScenarios
      .filter(rs => rs.scenario_id === selectedScenario)
      .map(rs => {
        const originalRisk = risks.find(r => r.id === rs.risk_id);
        return {
          ...originalRisk,
          ...rs,
          adjusted_risk_score: ((rs.adjusted_probability || 0) * (rs.adjusted_impact || 0)) / 100
        };
      });
  };

  const getTemperatureImpactData = () => {
    return scenarios.map(scenario => ({
      scenario: scenario.scenario_name,
      temperature: scenario.temperature_increase || 0,
      averageRisk: riskScenarios
        .filter(rs => rs.scenario_id === scenario.id)
        .reduce((sum, rs) => sum + ((rs.adjusted_probability || 0) * (rs.adjusted_impact || 0)) / 100, 0) / 
        Math.max(1, riskScenarios.filter(rs => rs.scenario_id === scenario.id).length)
    }));
  };

  const getCarbonPriceData = () => {
    return scenarios.map(scenario => ({
      scenario: scenario.scenario_name,
      carbonPrice: scenario.carbon_price || 0,
      transitionRisk: riskScenarios
        .filter(rs => rs.scenario_id === scenario.id)
        .filter(rs => {
          const risk = risks.find(r => r.id === rs.risk_id);
          return risk?.risk_type?.startsWith('transition');
        })
        .reduce((sum, rs) => sum + ((rs.adjusted_probability || 0) * (rs.adjusted_impact || 0)) / 100, 0)
    }));
  };

  const adjustedRisks = getScenarioAdjustedRisks();
  const tempData = getTemperatureImpactData();
  const carbonData = getCarbonPriceData();

  if (loading) {
    return <div className="flex items-center justify-center h-64">Loading scenario analysis...</div>;
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold">Climate Scenario Analysis</h2>
        <p className="text-muted-foreground">
          Analyze climate risks under different future scenarios and pathways
        </p>
      </div>

      {/* Scenario Selection */}
      <Card>
        <CardHeader>
          <CardTitle>Scenario Configuration</CardTitle>
          <CardDescription>Select and analyze climate scenarios</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Climate Scenario</label>
              <Select value={selectedScenario} onValueChange={setSelectedScenario}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a scenario" />
                </SelectTrigger>
                <SelectContent>
                  {scenarios.map((scenario) => (
                    <SelectItem key={scenario.id} value={scenario.id}>
                      {scenario.scenario_name} ({scenario.pathway.replace('_', ' ')})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-end">
              <Button 
                onClick={runScenarioAnalysis} 
                disabled={analyzing || !selectedScenario || risks.length === 0}
                className="w-full"
              >
                {analyzing ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Analyzing...
                  </>
                ) : (
                  <>
                    <TrendingUp className="h-4 w-4 mr-2" />
                    Run Analysis
                  </>
                )}
              </Button>
            </div>
          </div>

          {selectedScenario && scenarios.find(s => s.id === selectedScenario) && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-4 border-t">
              {(() => {
                const scenario = scenarios.find(s => s.id === selectedScenario)!;
                return (
                  <>
                    <div>
                      <p className="text-sm font-medium">Temperature Rise</p>
                      <p className="text-lg font-bold text-orange-600">
                        +{scenario.temperature_increase || 0}°C
                      </p>
                    </div>
                    <div>
                      <p className="text-sm font-medium">Carbon Price</p>
                      <p className="text-lg font-bold text-green-600">
                        ${scenario.carbon_price || 0}/tCO2
                      </p>
                    </div>
                    <div>
                      <p className="text-sm font-medium">Sea Level Rise</p>
                      <p className="text-lg font-bold text-blue-600">
                        +{scenario.sea_level_rise || 0}m
                      </p>
                    </div>
                    <div>
                      <p className="text-sm font-medium">Time Horizon</p>
                      <p className="text-lg font-bold text-purple-600">
                        {scenario.time_horizon} years
                      </p>
                    </div>
                  </>
                );
              })()}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Scenario Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Thermometer className="h-5 w-5" />
              Temperature vs Risk Impact
            </CardTitle>
            <CardDescription>Risk levels across temperature scenarios</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={tempData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="temperature" label={{ value: 'Temperature Rise (°C)', position: 'insideBottom', offset: -5 }} />
                <YAxis label={{ value: 'Average Risk Score', angle: -90, position: 'insideLeft' }} />
                <Tooltip />
                <Line type="monotone" dataKey="averageRisk" stroke="#ef4444" strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <DollarSign className="h-5 w-5" />
              Carbon Price Impact
            </CardTitle>
            <CardDescription>Transition risk under carbon pricing</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={carbonData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="carbonPrice" label={{ value: 'Carbon Price ($/tCO2)', position: 'insideBottom', offset: -5 }} />
                <YAxis label={{ value: 'Transition Risk', angle: -90, position: 'insideLeft' }} />
                <Tooltip />
                <Bar dataKey="transitionRisk" fill="#10b981" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Scenario-Adjusted Risks */}
      <Card>
        <CardHeader>
          <CardTitle>Scenario-Adjusted Risk Assessment</CardTitle>
          <CardDescription>
            {adjustedRisks.length} risk(s) analyzed under selected scenario
          </CardDescription>
        </CardHeader>
        <CardContent>
          {adjustedRisks.length === 0 ? (
            <div className="text-center py-8">
              <Cloud className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">No Scenario Analysis</h3>
              <p className="text-muted-foreground">
                Run scenario analysis to see adjusted risk assessments
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {adjustedRisks.map((risk: any) => (
                <Card key={risk.id}>
                  <CardHeader>
                    <div className="flex justify-between items-start">
                      <div>
                        <CardTitle className="text-lg">{risk.risk_name}</CardTitle>
                        <CardDescription>{risk.risk_category?.replace('_', ' ')}</CardDescription>
                      </div>
                      <Badge variant={risk.adjusted_risk_score >= 70 ? 'destructive' : risk.adjusted_risk_score >= 40 ? 'default' : 'secondary'}>
                        Adjusted Score: {risk.adjusted_risk_score?.toFixed(0) || 0}
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div>
                        <p className="text-sm font-medium">Original Risk Score</p>
                        <p className="text-sm text-muted-foreground">
                          {risk.risk_score?.toFixed(0) || 0}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm font-medium">Adjusted Probability</p>
                        <p className="text-sm text-muted-foreground">
                          {risk.adjusted_probability?.toFixed(0) || 0}%
                        </p>
                      </div>
                      <div>
                        <p className="text-sm font-medium">Adjusted Impact</p>
                        <p className="text-sm text-muted-foreground">
                          {risk.adjusted_impact?.toFixed(0) || 0}%
                        </p>
                      </div>
                      <div>
                        <p className="text-sm font-medium">Financial Impact</p>
                        <p className="text-sm text-muted-foreground">
                          ${risk.adjusted_financial_impact_low?.toLocaleString() || 0} - ${risk.adjusted_financial_impact_high?.toLocaleString() || 0}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Standard Scenarios Info */}
      <Card>
        <CardHeader>
          <CardTitle>Available Climate Scenarios</CardTitle>
          <CardDescription>Standard scenarios for climate risk assessment</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {scenarios.filter(s => s.is_standard_scenario).map((scenario) => (
              <Card key={scenario.id}>
                <CardHeader>
                  <CardTitle className="text-lg">{scenario.scenario_name}</CardTitle>
                  <CardDescription>{scenario.pathway.replace('_', ' ')}</CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground mb-4">{scenario.description}</p>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div>
                      <span className="font-medium">Temperature:</span> +{scenario.temperature_increase}°C
                    </div>
                    <div>
                      <span className="font-medium">Carbon Price:</span> ${scenario.carbon_price}/tCO2
                    </div>
                    <div>
                      <span className="font-medium">Sea Level:</span> +{scenario.sea_level_rise}m
                    </div>
                    <div>
                      <span className="font-medium">Timeline:</span> {scenario.time_horizon} years
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
